import { useTranslation } from 'react-i18next';
import { useGet } from '@/query/fetchHooks';
import { useState, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, IconTile, ProgressBar } from '@teo/components';
import {
  Theme,
  TimeFilled,
  Shapes,
  Training,
  Evaluation,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
} from '@teo/components/icons';
import { convertToTime } from '@/util';
import { NavContext } from '@/components/NavContext';
import { sortDataLP } from '@/components/LearningPath/util';

function ThemePreview() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<any>(null);
  const { themeId, organisationId } = useParams();
  const [nodes, setNodes] = useState<any>(null);
  const { setHeaderDisabled, setLeftSidebar } = useContext(NavContext);

  useEffect(() => {
    setLeftSidebar(true);
    return () => setLeftSidebar(false);
  }, []);

  useEffect(() => {
    setHeaderDisabled(true);
    return () => setHeaderDisabled(false);
  });

  useGet(`/learningpath/${themeId}`, {
    onSuccess: async (res: any) => {
      setTheme(res?.data || {});
    },
  });

  useGet(`/learningpathnodedetails`, {
    learningpathId: themeId,
    organisationId: organisationId,
    onSuccess: async (res: any) => {
      const nodedetails = (res?.data || []).filter(
        (el: any) => el?.type !== 'root'
      );
      const sortData = sortDataLP(nodedetails);
      setNodes(sortData);
    },
  });
  return (
    <div className="-ml-4 -mr-4 block md:ml-0 md:mr-0">
      <div className="mx-auto flex w-full max-w-3xl flex-col">
        <div className="flex w-full flex-col border-b px-4 md:px-0">
          <div className="flex pt-4">
            <IconTile Icon={Theme} color={'dark_blue'} size="small_lg" />
          </div>
          <div className="flex flex-col pt-4">
            <p className="text-sm text-grey-05">
              {t('pages.landing_training.theme')}
            </p>
            <p className={`pt-1 pb-4 text-xl font-semibold text-black`}>
              {theme?.lpName}
            </p>

            <div className="my-4 flex items-center">
              <div className="flex w-full">
                <ProgressBar
                  max={100}
                  value={0}
                  className="h-1 overflow-hidden rounded-lg"
                />
              </div>
              <span className="pl-3 text-xs text-grey-06">{0}%</span>
            </div>
            <p className={`text-base text-grey-08  `}>{theme?.lpDescription}</p>

            <div className="mt-4 flex gap-4">
              {nodes?.length > 0 && (
                <div className="flex items-center">
                  <Shapes className="h-4 w-4 text-grey-04" />
                  <p className="pl-1 text-sm text-grey-05">
                    {nodes?.length} {t('pages.lp_landing.items')}
                  </p>
                </div>
              )}

              {/*theme?.lpEstimatedTime > 0 && (
                <div className="flex items-center">
                  <TimeFilled className="h-4 w-4 text-grey-04" />
                  <p className="pl-1 text-sm text-grey-05">
                    {convertToTime(
                      Math.abs(theme?.lpEstimatedTime),
                      t('pages.learn_index.hour'),
                      t('pages.learn_index.minute')
                    )}
                  </p>
                </div>
              )*/}
            </div>
          </div>
          <div className="my-8">
            <Button>{t('pages.lp_landing.btn_start')}</Button>
          </div>
        </div>
        <div className="my-8 flex w-full flex-col px-4 md:px-0">
          <p className={`mb-3 text-base font-medium `}>
            {t('pages.course_landing.contents')}
          </p>
          <div className="flex flex-col gap-3">
            {nodes?.map((node: any, index: number) => {
              return (
                <div
                  key={node?.id}
                  className={`flex w-full items-center rounded-lg pr-2 `}
                >
                  <IconTile
                    Icon={
                      node.subType === 'training'
                        ? Training
                        : node.subType === 'evaluation'
                        ? Evaluation
                        : node.subType === 'course'
                        ? DuoToneCourseFilled
                        : node.subType === 'assessment'
                        ? DuoToneAssessmentFilled
                        : node.subType === 'leervacature'
                        ? DuoToneVacancyFilled
                        : DuoToneCourseFilled
                    }
                    imageUrl={node.picture}
                    color={
                      node.subType === 'training'
                        ? 'green'
                        : node.subType === 'evaluation'
                        ? 'purple'
                        : node.subType === 'course'
                        ? 'yellow'
                        : node.subType === 'assessment'
                        ? 'red'
                        : node.subType === 'leervacature'
                        ? 'yellow'
                        : 'yellow'
                    }
                    size="small_lg"
                    className={
                      node.subType === 'course' || node.subType === 'import'
                        ? 'fill-primary-06 text-primary-01'
                        : node.subType === 'assessment'
                        ? 'fill-tint-red-dark text-tint-red-light'
                        : node.subType === 'leervacature'
                        ? 'fill-error-04 text-error-04'
                        : ''
                    }
                  />
                  <div className="mx-4 flex flex-col">
                    <h4>
                      {index + 1}. {node?.name}
                    </h4>
                    <p className="mt-1 text-sm text-grey-04">
                      {node.subType === 'training'
                        ? t('pages.learning_path.training')
                        : node.subType === 'evaluation'
                        ? t('pages.learning_path.evaluation')
                        : node.subType === 'course'
                        ? t('links.course')
                        : node.subType === 'assessment'
                        ? t('links.assessment')
                        : node.subType === 'leervacature'
                        ? t('links.vacancy')
                        : t('links.course')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThemePreview;
